/**
 * 결정형 채움 링 (RFC-progress-ring). 트랙 원 위에 `value` 비율만큼 arc를 그린다.
 *
 * Owns:
 * - `value`(0~1) → arc 길이. 생략하면 indeterminate: 고정 arc가 회전한다.
 * - glyph `size`(sm/md/lg/xl) → 타입 스케일 토큰 매핑(Spinner와 같은 축).
 * - `label` 유무에 따른 a11y 모드(있으면 `role=progressbar`, 없으면 장식용 aria-hidden).
 *
 * Does not own:
 * - 색: arc는 `currentColor`를 상속한다. 상태 색은 호출 컨텍스트가 text 색으로 소유한다.
 * - 안쪽 원 채움·중앙 콘텐츠·배치: 소비처가 감싸서 소유한다.
 *
 * Spinner와의 경계: Spinner는 "진행률을 모르는 로딩"이고, ProgressRing은 비율 또는 상태를
 * 채움 양으로 표현한다. 정지된 완전한 링(value=1)은 로딩이 아니다.
 */
import type { CSSProperties } from "react";
import styled, { css, keyframes } from "styled-components";

import { type GlyphSize, type GlyphSizeToken, glyphSizeToken } from "../foundations/glyphSize";
import { devWarn } from "../../utils/devWarning";

export type ProgressRingSize = GlyphSize;

export type ProgressRingProps = {
  /** 0~1 결정형 채움 비율. 생략하면 indeterminate(고정 arc 회전). 범위 밖은 clamp한다. */
  value?: number;
  size?: ProgressRingSize;
  /** 있으면 접근성 이름(role=progressbar). 옆 텍스트가 상태를 전달하면 생략해 장식 처리. */
  label?: string;
  className?: string;
};

// 24 그리드, 반지름 10, 선 두께 4 — 바깥 가장자리가 viewBox에 닿고 띠 폭은 지름의 1/6이다(SigmaKit 센서 링의
// 테두리+arc 띠 폭과 같다). 트랙과 arc가 같은 지오메트리를 공유한다.
const RADIUS = 10;
const STROKE_WIDTH = 4;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const INDETERMINATE_ARC = 0.3;
const VALUE_VAR = "--c6-progress-ring-value";

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const Ring = styled.svg<{ $token: GlyphSizeToken; $indeterminate: boolean }>`
  display: inline-block;
  flex: 0 0 auto;
  width: 1em;
  height: 1em;
  font-size: ${({ theme, $token }) => theme.typography.size[$token]};
  color: inherit;
  overflow: visible;

  ${({ $indeterminate }) =>
    $indeterminate &&
    css`
      animation: ${spin} 1.5s linear infinite;

      /* reduced-motion: 회전은 "아직 진행 중"을 알리는 피드백이라 멈추지 않고 감속한다(Spinner와 같은 판단). */
      @media (prefers-reduced-motion: reduce) {
        animation-duration: 3s;
      }
    `}
`;

const Track = styled.circle`
  fill: none;
  stroke: ${({ theme }) => theme.color.border.subtle};
`;

// 채움 비율은 고빈도로 바뀔 수 있어 styled 템플릿에 보간하지 않고 inline CSS 변수로 받는다(§5-E).
const Arc = styled.circle`
  fill: none;
  stroke: currentColor;
  stroke-dasharray: ${CIRCUMFERENCE}px;
  stroke-dashoffset: calc(${CIRCUMFERENCE}px * (1 - var(${VALUE_VAR}, 0)));
  transform: rotate(-90deg);
  transform-box: view-box;
  transform-origin: center;
`;

function resolveValue(value: number | undefined): number | undefined {
  if (value === undefined) return undefined;
  if (!Number.isFinite(value)) {
    devWarn(`ProgressRing: value must be a finite number in [0, 1]; received ${String(value)}.`);
    return 0;
  }
  return Math.min(1, Math.max(0, value));
}

export function ProgressRing({ value, size = "md", label, className }: ProgressRingProps) {
  const resolved = resolveValue(value);
  const indeterminate = resolved === undefined;
  const fraction = indeterminate ? INDETERMINATE_ARC : resolved;

  return (
    <Ring
      viewBox="0 0 24 24"
      $token={glyphSizeToken[size]}
      $indeterminate={indeterminate}
      className={className}
      style={{ [VALUE_VAR]: fraction } as CSSProperties}
      role={label ? "progressbar" : undefined}
      aria-label={label || undefined}
      aria-valuemin={label ? 0 : undefined}
      aria-valuemax={label ? 1 : undefined}
      aria-valuenow={label && !indeterminate ? resolved : undefined}
      aria-hidden={label ? undefined : true}
    >
      <Track cx="12" cy="12" r={RADIUS} strokeWidth={STROKE_WIDTH} />
      <Arc cx="12" cy="12" r={RADIUS} strokeWidth={STROKE_WIDTH} />
    </Ring>
  );
}

ProgressRing.displayName = "ProgressRing";
